import { useMemo } from 'react';
import { SEARCH_TYPE } from '@/constants/searchType';
import {
  BasicSearchAnimalFields,
  BasicSearchDetailAnimalFields,
  getAllFieldOptionsForSearchType,
  getAnimalBasicSearchFields,
  getMemberBasicSearchFields,
  getSearchFieldIdentity,
} from '../constants';
import { EBV_TAB_VALUE } from './basicSearchDialogUtils';
import type { EbvConfigItem } from '@/hooks/publicSearch/ebv/ebvColumns';
import type { Society } from '@/publicSearch/store/usePublicSearchContext';
import type { FilterInput } from '../../constants';

/** Unrendered saved filters must stay visible as removable additional rows. */
export function partitionBasicSearchFilters(
  filters: FilterInput[],
  renderedIdentities: Set<string>,
) {
  return {
    fixed: filters.filter((filter) => renderedIdentities.has(filter.field)),
    dynamic: filters.filter((filter) => !renderedIdentities.has(filter.field)),
  };
}

interface UseBasicSearchFieldsParams {
  searchType: string;
  isDetailAnimalsSearch: boolean;
  tabValue: string;
  societySettings: Society['settings'];
  effectiveEbvConfig: EbvConfigItem[];
  ebvLabel: string;
  showPercentileSearch: boolean;
}

export function useBasicSearchFields({
  searchType,
  isDetailAnimalsSearch,
  tabValue,
  societySettings,
  effectiveEbvConfig,
  ebvLabel,
  showPercentileSearch,
}: UseBasicSearchFieldsParams) {
  const fields = useMemo(() => {
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      const base =
        searchType === SEARCH_TYPE.ANIMAL
          ? BasicSearchAnimalFields
          : BasicSearchDetailAnimalFields;
      return getAnimalBasicSearchFields(societySettings, base);
    }
    if (searchType === SEARCH_TYPE.MEMBERS) {
      return getMemberBasicSearchFields(societySettings);
    }
    return [];
  }, [isDetailAnimalsSearch, searchType, societySettings]);

  const fieldValues = useMemo(() => {
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      // Include EBV fields if on EBV tab
      if (tabValue === EBV_TAB_VALUE) {
        const ebvFieldValues: string[] = [];
        effectiveEbvConfig
          .filter((item) => item && item.display > 0 && item.field)
          .forEach((item) => {
            const fieldNumber = item.field.replace('ebv', '');
            // Use lowercase to match GraphQL schema field names
            ebvFieldValues.push(`ebv${fieldNumber}`); // EBV value field
            ebvFieldValues.push(`ebv${fieldNumber}Acc`); // EBV accuracy field
            ebvFieldValues.push(`ebv${fieldNumber}Percentile`); // EBV percentile field
          });
        return ebvFieldValues;
      }
      return fields.map(({ value }) => value);
    }
    if (searchType === SEARCH_TYPE.MEMBERS)
      return fields.map(({ value }) => value);
    return [];
  }, [searchType, tabValue, effectiveEbvConfig, fields, isDetailAnimalsSearch]);

  const allFixedFieldValues = useMemo(() => {
    const values = new Set<string>();
    fields.forEach((f) => {
      values.add(getSearchFieldIdentity(f));
    });
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      effectiveEbvConfig
        .filter((item) => item && item.display > 0 && item.field)
        .forEach((item) => {
          const n = item.field.replace('ebv', '');
          values.add(`ebv${n}`);
          values.add(`ebv${n}Acc`);
          values.add(`ebv${n}Percentile`);
        });
    }
    return values;
  }, [fields, effectiveEbvConfig, searchType, isDetailAnimalsSearch]);

  const dynamicFieldOptions = useMemo(
    () =>
      // For members, MEMBER_SEARCH_FIELDS (SocietySettingType=19) drives
      // both which fields appear and their labels — same source feeding
      // the advanced filter pill dropdown.
      getAllFieldOptionsForSearchType(
        searchType,
        fields,
        effectiveEbvConfig,
        allFixedFieldValues,
        societySettings,
        ebvLabel,
        showPercentileSearch,
      ),
    [
      searchType,
      fields,
      effectiveEbvConfig,
      allFixedFieldValues,
      societySettings,
      ebvLabel,
      showPercentileSearch,
    ],
  );

  return { fields, fieldValues, allFixedFieldValues, dynamicFieldOptions };
}
